/**
 * Custom Assembly Type Configuration Section
 * Shown whenever an Assembly Type is selected. Allows defining optional per-assembly
 * column definitions and values at installation (assembly + airframe). When a master
 * config exists, custom column names must not duplicate its column names.
 */

import React, { useState } from 'react';
import {
  Grid,
  TextField,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Divider
} from '@mui/material';
import Add from '@mui/icons-material/Add';
import Remove from '@mui/icons-material/Remove';

const COMMON_DATASET_TEMPLATES = {
  landings: { name: 'landings', label: 'Number of Landings', type: 'number', unit: 'Nos' },
  cycles: { name: 'cycles', label: 'Number of Cycles', type: 'number', unit: 'Cls' },
  hours: { name: 'hours', label: 'Hours', type: 'number', unit: 'Hrs' },
  years: { name: 'years', label: 'Number of Years', type: 'number', unit: 'Yrs' },
  days: { name: 'days', label: 'Number of Days', type: 'number', unit: 'Days' },
  months: { name: 'months', label: 'Number of Months', type: 'number', unit: 'Months' }
};

const CustomAssemblyTypeConfigurationSection = ({
  formData,
  assemblyTypeConfig,
  configLoading,
  handleAddCustomColumn,
  handleRemoveCustomColumn,
  handleCustomColumnChange,
  handleCustomColumnApplyTemplate,
  getCustomConfigValue,
  handleCustomConfigValueChange
}) => {
  const [commonDatasetSelections, setCommonDatasetSelections] = useState({});

  if (!formData.type) {
    return null;
  }
  if (configLoading) {
    return null;
  }

  const hasTypeConfig = assemblyTypeConfig && assemblyTypeConfig.columns && assemblyTypeConfig.columns.length > 0;
  const configColumnNames = (assemblyTypeConfig?.columns || []).map(c => (c.name || '').trim().toLowerCase());
  const customColumns = Array.isArray(formData.customColumns) ? formData.customColumns : [];
  const sortedColumns = [...customColumns].sort((a, b) => (a.order || 0) - (b.order || 0));

  const isDuplicateConfigColumnName = (name) => {
    if (!name || !hasTypeConfig) return false;
    return configColumnNames.includes(String(name).trim().toLowerCase());
  };

  return (
    <>
      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h6" sx={{ mb: 2, fontSize: '16px', fontWeight: 600 }}>
          Custom Assembly Type Configuration
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {hasTypeConfig
            ? 'Optional: define additional columns and values for this assembly only. Custom column names must not duplicate the Assembly Type Configuration column names above.'
            : 'No master configuration exists for this Assembly Type. Define columns and values at installation below.'}
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Column Definitions
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Add />}
            onClick={handleAddCustomColumn}
          >
            Add Column
          </Button>
        </Box>

        {customColumns.length > 0 ? (
          <Box sx={{ mb: 2 }}>
            {customColumns.map((column, index) => (
              <Card key={index} sx={{ mb: 2, border: '1px solid', borderColor: 'divider' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Column {index + 1}
                    </Typography>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveCustomColumn(index)}
                      disabled={customColumns.length <= 1}
                    >
                      <Remove />
                    </IconButton>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Common dataset</InputLabel>
                      <Select
                        value={commonDatasetSelections[index] || ''}
                        label="Common dataset"
                        onChange={(e) => {
                          const key = e.target.value;
                          setCommonDatasetSelections(prev => ({ ...prev, [index]: '' }));
                          if (key && COMMON_DATASET_TEMPLATES[key]) {
                            handleCustomColumnApplyTemplate(index, COMMON_DATASET_TEMPLATES[key]);
                          }
                        }}
                        displayEmpty
                      >
                        <MenuItem value="">
                          <em>Select a template to auto-fill</em>
                        </MenuItem>
                        {Object.keys(COMMON_DATASET_TEMPLATES).map(key => (
                          <MenuItem key={key} value={key}>
                            {COMMON_DATASET_TEMPLATES[key].label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Name *"
                        value={column.name || ''}
                        onChange={(e) => handleCustomColumnChange(index, 'name', e.target.value)}
                        placeholder="e.g., hours"
                        error={isDuplicateConfigColumnName(column.name)}
                        helperText={isDuplicateConfigColumnName(column.name) ? 'Must not duplicate an Assembly Type Configuration column name' : undefined}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Label *"
                        value={column.label || ''}
                        onChange={(e) => handleCustomColumnChange(index, 'label', e.target.value)}
                        placeholder="e.g., Hours"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small" required>
                        <InputLabel>Type *</InputLabel>
                        <Select
                          value={column.type || 'text'}
                          label="Type *"
                          onChange={(e) => handleCustomColumnChange(index, 'type', e.target.value)}
                        >
                          <MenuItem value="text">Text</MenuItem>
                          <MenuItem value="number">Number</MenuItem>
                          <MenuItem value="date">Date</MenuItem>
                          <MenuItem value="time">Time</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Unit"
                        value={column.unit || ''}
                        onChange={(e) => handleCustomColumnChange(index, 'unit', e.target.value)}
                        placeholder="e.g., Hrs"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Order *"
                        value={column.order ?? index + 1}
                        onChange={(e) => handleCustomColumnChange(index, 'order', parseInt(e.target.value, 10) || index + 1)}
                        inputProps={{ min: 1 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={Boolean(column.required)}
                            onChange={(e) => handleCustomColumnChange(index, 'required', e.target.checked)}
                          />
                        }
                        label="Required"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
            No columns defined. Click &quot;Add Column&quot; to define values at installation (e.g. Hours, Cycles).
          </Typography>
        )}
      </Grid>

      {sortedColumns.length > 0 && (
        <Grid item xs={12}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
            Values at Installation
          </Typography>
          <Box sx={{ mt: 1 }}>
            {sortedColumns.map((column, idx) => (
              <Grid container spacing={2} key={`custom-val-${idx}-${(column.name || column.order) ?? idx}`} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label={`${column.label || column.name || 'Column'} (Assembly Value)${column.required ? ' *' : ''}`}
                    value={getCustomConfigValue(column.name, 'assemblyValue')}
                    onChange={(e) => handleCustomConfigValueChange(column.name, 'assemblyValue', e.target.value)}
                    type={column.type === 'number' ? 'number' : column.type === 'date' ? 'date' : column.type === 'time' ? 'time' : 'text'}
                    InputLabelProps={column.type === 'date' || column.type === 'time' ? { shrink: true } : undefined}
                    helperText={column.unit ? `Unit: ${column.unit}` : undefined}
                    sx={{ minWidth: 280 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label={`${column.label || column.name || 'Column'} (Airframe Value)${column.required ? ' *' : ''}`}
                    value={getCustomConfigValue(column.name, 'airframeValue')}
                    onChange={(e) => handleCustomConfigValueChange(column.name, 'airframeValue', e.target.value)}
                    type={column.type === 'number' ? 'number' : column.type === 'date' ? 'date' : column.type === 'time' ? 'time' : 'text'}
                    InputLabelProps={column.type === 'date' || column.type === 'time' ? { shrink: true } : undefined}
                    helperText={column.unit ? `Unit: ${column.unit}` : undefined}
                    sx={{ minWidth: 280 }}
                  />
                </Grid>
              </Grid>
            ))}
          </Box>
        </Grid>
      )}
    </>
  );
};

export default CustomAssemblyTypeConfigurationSection;
